import { useState, useRef } from 'react';
import { Image as ImageIcon, X } from 'lucide-react';
import { useUploadBlogImage } from '@/lib/api/hooks/use-blogs';

interface ImageUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImageInsert: (src: string) => void;
}

export function ImageUploadDialog({ isOpen, onClose, onImageInsert }: ImageUploadDialogProps) {
  const [imageUrl, setImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadImage = useUploadBlogImage();


  if (!isOpen) {
    return null;
  }


  const handleInsertImage = () => {
    if (imageUrl.trim()) {
      onImageInsert(imageUrl);
      setImageUrl('');
      onClose();
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadImage.mutateAsync(file);
      onImageInsert(result.url);
      onClose();
    } catch (error) {
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Add Image</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Choose how you'd like to add an image to your blog post
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            disabled={isUploading}
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* URL Input Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Image URL</label>
            <input
              type="url"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleInsertImage();
                }
                if (e.key === 'Escape') {
                  onClose();
                }
              }}
              className="w-full px-4 py-3 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              autoFocus
              disabled={isUploading}
            />
            <button
              onClick={handleInsertImage}
              disabled={!imageUrl.trim() || isUploading}
              className="w-full px-4 py-2 text-sm rounded-lg text-center transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Insert from URL
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center">
            <div className="flex-1 border-t border-border"></div>
            <span className="px-3 text-xs text-muted-foreground bg-card">OR</span>
            <div className="flex-1 border-t border-border"></div>
          </div>

          {/* File Upload Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Upload from Computer</label>
            <div 
              className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => !isUploading && fileInputRef.current?.click()}
            >
              <ImageIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-3">
                {isUploading ? 'Uploading image...' : 'Click to select an image file'}
              </p>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isUploading) {
                    fileInputRef.current?.click();
                  }
                }}
                disabled={isUploading}
                className={`px-6 py-2 text-sm rounded-lg transition-colors font-medium ${
                  isUploading 
                    ? 'bg-muted text-muted-foreground cursor-not-allowed' 
                    : 'bg-accent text-foreground hover:bg-accent/80 cursor-pointer'
                }`}
              >
                {isUploading ? 'Uploading...' : 'Choose File'}
              </button>
              <p className="text-xs text-muted-foreground mt-2">
                Supports JPG, PNG, GIF up to 10MB
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              disabled={isUploading}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-border bg-muted/30">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm rounded-lg text-center transition-colors bg-muted text-muted-foreground hover:bg-muted/80 font-medium"
            disabled={isUploading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
